// Ctrl+K on a ticket page: the ticket actions you can type.
//
// "resolve", "snooze tom", "assign liz", "p1", "next" ... This decides WHICH
// actions make sense for this ticket and this person (permissions, current
// status, who it is assigned to); components/CommandPalette.tsx renders them and
// runs them through useTicketAction, so the dispatcher stays the single source of
// action logic (CLAUDE.md §5).
//
// Pure on purpose: tests/palette-actions.test.ts.

export interface PaletteActionTicket {
  id: string;
  subject: string | null;
  status: string | null;
  priority: string | null;
  assignee_slack_id: string | null;
  snoozed_until: string | null;
  archived_at: string | null;
  inbox_id: string;
}

export interface PaletteActionCtx {
  ticket: PaletteActionTicket;
  mySlackId: string | null;
  members: ReadonlyArray<{ slack_user_id: string; name: string; active?: boolean | null }>;
  /** Permission check for THIS ticket's inbox (e.g. 'tickets.status'). */
  can: (perm: string) => boolean;
  now?: number;
  /** The agent came from a list, so "open next" makes sense. */
  hasList: boolean;
  hasNext: boolean;
  hasPrev: boolean;
}

export type PaletteIntent =
  | { type: 'action'; action: string; payload?: Record<string, unknown>; advance?: boolean }
  | { type: 'nav'; to: 'next' | 'prev' }
  | { type: 'mark-unread' }
  | { type: 'remind'; preset: 'tomorrow' | '2d' | '1w' };

export interface PaletteAction {
  id: string;
  label: string;
  /** Extra words that should find this action ("later" finds the snoozes). */
  keywords?: string;
  intent: PaletteIntent;
}

const PRIORITIES = ['P1', 'P2', 'P3', 'P4'] as const;

const SNOOZES: Array<{ action: string; label: string }> = [
  { action: 'snooze_1h', label: 'Snooze for 1 hour' },
  { action: 'snooze_4h', label: 'Snooze for 4 hours' },
  { action: 'snooze_tomorrow', label: 'Snooze until tomorrow 9am' },
  { action: 'snooze_2d', label: 'Snooze for 2 days' },
  { action: 'snooze_monday', label: 'Snooze until Monday 9am' },
];

export function ticketActionsFor(c: PaletteActionCtx): PaletteAction[] {
  const t = c.ticket;
  const now = c.now ?? Date.now();
  const closed = t.status === 'resolved' || t.status === 'closed';
  const snoozed = !!t.snoozed_until && new Date(t.snoozed_until).getTime() > now;
  const canStatus = c.can('tickets.status');
  const out: PaletteAction[] = [];
  const add = (id: string, label: string, intent: PaletteIntent, keywords?: string) =>
    out.push({ id, label, intent, keywords });

  if (canStatus && !closed) {
    add('resolve', 'Resolve', { type: 'action', action: 'resolve' }, 'done finish close complete');
    if (c.hasList) {
      add('resolve-next', 'Resolve and open next ticket', { type: 'action', action: 'resolve', advance: true }, 'done finish');
    }
    add('dismiss', 'Dismiss', { type: 'action', action: 'close' }, 'spam no action ignore marketing');
  }
  if (canStatus && closed) {
    add('reopen', 'Reopen', { type: 'action', action: 'reopen' }, 'open again restore');
  }

  if (c.can('tickets.claim') && c.mySlackId && t.assignee_slack_id !== c.mySlackId) {
    add('claim', 'Claim (assign to me)', { type: 'action', action: 'claim' }, 'take mine own');
  }
  if (c.can('tickets.assign_others')) {
    for (const m of c.members) {
      if (m.active === false) continue;
      if (m.slack_user_id === t.assignee_slack_id) continue;
      add(
        'assign-' + m.slack_user_id,
        `Assign to ${m.name}`,
        { type: 'action', action: 'reassign', payload: { reassign_to: m.slack_user_id } },
        'reassign give hand over',
      );
    }
  }

  if (canStatus && !closed) {
    if (snoozed) {
      add('unsnooze', 'Wake up now', { type: 'action', action: 'unsnooze' }, 'unsnooze resurface');
    } else {
      for (const s of SNOOZES) add(s.action, s.label, { type: 'action', action: s.action }, 'later hide remind');
    }
    if (t.priority !== 'P1') add('escalate', 'Escalate', { type: 'action', action: 'escalate' }, 'urgent raise priority');
  }
  if (canStatus) {
    for (const p of PRIORITIES) {
      if (p === t.priority) continue;
      add('priority-' + p, `Set priority ${p}`, { type: 'action', action: 'set_priority', payload: { priority: p } }, 'urgent');
    }
  }

  if (c.can('tickets.archive')) {
    if (t.archived_at) add('unarchive', 'Unarchive', { type: 'action', action: 'unarchive' }, 'restore');
    else add('archive', 'Archive', { type: 'action', action: 'archive' }, 'hide');
  }

  // Personal follow-up reminders (9am, if the client hasn't replied). No permission needed.
  if (!closed) {
    add('remind-tomorrow', 'Remind me tomorrow 9am', { type: 'remind', preset: 'tomorrow' }, 'follow up later');
    add('remind-2d', 'Remind me in 2 days', { type: 'remind', preset: '2d' }, 'follow up later');
    add('remind-1w', 'Remind me in a week', { type: 'remind', preset: '1w' }, 'follow up later');
  }

  // Personal (only changes your own list), so it needs no permission.
  add('mark-unread', 'Mark as unread', { type: 'mark-unread' }, 'bold later come back');

  if (c.hasNext) add('next', 'Next ticket', { type: 'nav', to: 'next' }, 'forward');
  if (c.hasPrev) add('prev', 'Previous ticket', { type: 'nav', to: 'prev' }, 'back');
  return out;
}

/** Every word typed must appear in the label or its keywords. Nothing shows for an
 *  empty box: actions are something you ask for, not something that clutters Ctrl+K. */
export function filterActions(actions: readonly PaletteAction[], query: string, limit = 8): PaletteAction[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return actions
    .filter((a) => {
      const hay = (a.label + ' ' + (a.keywords ?? '')).toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .slice(0, limit);
}
